import { getNumber, getString, toInsertLocation } from "./utils";

type SearchOpts = {
  matchCase?: boolean;
  matchWholeWord?: boolean;
  matchPrefix?: boolean;
  matchSuffix?: boolean;
  matchWildcards?: boolean;
  ignorePunct?: boolean;
  ignoreSpace?: boolean;
};

function toSearchOptions(
  args: Record<string, unknown>
): SearchOpts | undefined {
  const raw = args.options;
  if (!raw || typeof raw !== "object") {
    return undefined;
  }
  const o = raw as Record<string, unknown>;
  const out: SearchOpts = {};
  for (const key of [
    "matchCase",
    "matchWholeWord",
    "matchPrefix",
    "matchSuffix",
    "matchWildcards",
    "ignorePunct",
    "ignoreSpace",
  ] as const) {
    if (typeof o[key] === "boolean") {
      out[key] = o[key] as boolean;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export async function searchDocumentCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<{
  matches: Array<{ text: string; paragraphText: string }>;
  total: number;
  truncated: boolean;
}> {
  const searchText = getString(args, "searchText");
  const options = toSearchOptions(args);
  const limit = getNumber(args, "limit", false) ?? 200;
  const results = ctx.document.body.search(searchText, options);
  results.load("items/text");
  await ctx.sync();
  const all = results.items;
  const slice = all.slice(0, limit);
  const paraRanges = slice.map((r) => r.paragraphs.getFirstOrNullObject());
  for (const p of paraRanges) {
    p.load("text,isNullObject");
  }
  await ctx.sync();
  return {
    matches: slice.map((r, i) => ({
      text: r.text ?? "",
      paragraphText:
        paraRanges[i] && !paraRanges[i]?.isNullObject
          ? (paraRanges[i]?.text ?? "")
          : "",
    })),
    total: all.length,
    truncated: all.length > slice.length,
  };
}

export async function findReplaceCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<{ replacements: number }> {
  const searchText = getString(args, "searchText");
  const replaceText = (args.replaceText ?? "") as string;
  if (typeof replaceText !== "string") {
    throw new Error("Argument replaceText must be a string.");
  }
  const options = toSearchOptions(args);
  const results = ctx.document.body.search(searchText, options);
  results.load("items");
  await ctx.sync();
  for (const r of results.items) {
    r.insertText(replaceText, toInsertLocation("replace"));
  }
  return { replacements: results.items.length };
}
